import React, { useEffect, useState, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../AuthContext';
import { db } from '../../firebase';
import { doc, getDoc, collection, query, where, getDocs, updateDoc } from 'firebase/firestore';
import './CustomSuccessPage.css';

const CustomSuccessPage = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [requestDetails, setRequestDetails] = useState(null);
    const { user } = useContext(AuthContext);
    const location = useLocation();
    const navigate = useNavigate();
    const [sessionId, setSessionId] = useState(null);
    const [userId, setUserId] = useState(null);
    const [attempts, setAttempts] = useState(0);

    // Parse URL parameters and wait for auth
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const sid = params.get('session_id');
        const uid = params.get('user_id');
        
        if (sid) {
            // Session ID parsed from URL
            setSessionId(sid);
            sessionStorage.setItem('customContentSessionId', sid);
        }
        
        if (uid) {
            // User ID parsed from URL
            setUserId(uid);
            sessionStorage.setItem('customContentUserId', uid);
        }

        // Only fetch details if we have both user auth and session ID
        if (user && sid) {
            fetchRequestDetails(user.uid, sid);
        } else if (sid && attempts < 5) {
            // If we don't have user auth yet, wait and try again
            console.log(`Waiting for auth... Attempt ${attempts + 1}`);
            const timer = setTimeout(() => {
                setAttempts(prev => prev + 1);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [location, user, attempts]);

    const fetchRequestDetails = async (uid, sid) => {
        if (!uid || !sid) return;
        
        try {
            // Fetching request details
            
            // First check if user has permission
            const userDoc = await getDoc(doc(db, 'users', uid));
            if (!userDoc.exists()) {
                throw new Error('User not found');
            }

            const requestsRef = collection(db, 'custom_content_requests');
            const q = query(requestsRef, 
                where('userId', '==', uid),
                where('checkoutSessionId', '==', sid)
            );
            
            const querySnapshot = await getDocs(q);
            console.log('Found request documents:', querySnapshot.size);
            
            if (querySnapshot.empty) {
                console.log("No matching request found");
                setError("We couldn't find your order details. Please contact support.");
                setLoading(false);
                return;
            }
            
            const requestDoc = querySnapshot.docs[0];
            console.log('Current request data:', requestDoc.data());
            
            // Update the request status to 'paid'
            const updateData = {
                status: 'paid',
                checkoutSessionId: sid,
                paidAt: new Date(),
                fulfilled: false // Ensure fulfilled field exists and is false
            };
            console.log('Updating request with:', updateData);
            
            await updateDoc(doc(db, 'custom_content_requests', requestDoc.id), updateData);
            
            const updatedData = {
                ...requestDoc.data(),
                status: 'paid',
                checkoutSessionId: sid,
                paidAt: new Date()
            };
            
            console.log("Updated request details:", updatedData);
            setRequestDetails(updatedData);
            setLoading(false);
            
        } catch (err) {
            console.error("Error fetching/updating request details:", err);
            setError("An error occurred while retrieving your order details.");
            setLoading(false);
        }
    };

    // Show animation when component is ready
    useEffect(() => {
        if (!loading && !error) {
            const container = document.querySelector('.success-container');
            if (container) {
                setTimeout(() => {
                    container.classList.add('show');
                }, 100);
            }
        }
    }, [loading, error]);

    const handleViewPurchased = () => {
        // Navigate to auth-home with explicit state for CustomBuild component
        navigate('/auth-home', { 
            state: { 
                targetComponent: 'custom_build',
                targetTab: 'tracking'  // This matches the tab ID in CustomBuild.js
            },
            replace: true  // Add this to replace the current history entry
        });
    };

    if (loading) {
        return (
            <div className="custom-success-page">
                <div className="success-container loading-container show">
                    <h2>Processing your order...</h2>
                    <div className="loading-spinner"></div>
                    <p>This may take a few moments. Please don't close this page.</p>
                    {attempts >= 3 && (
                        <button onClick={() => navigate('/auth-home')} className="manual-return-button">
                            Return to Dashboard
                        </button>
                    )}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="custom-success-page">
                <div className="success-container error">
                    <h2>Something went wrong</h2>
                    <p>{error}</p>
                    <p>If your payment was successful, your custom content will still be created.</p>
                    <div className="error-details">
                        <p>Session ID: {sessionId || 'Not found'}</p>
                        <p>User ID: {userId || (user ? user.uid : 'Not found')}</p>
                    </div>
                    <Link to="/auth-home" className="return-button">
                        Return to Dashboard
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="custom-success-page">
            <div className="success-container">
                <div className="success-icon">
                    <svg viewBox="0 0 24 24" width="100" height="100">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                    </svg>
                </div>
                <h2>Custom Content Order Confirmed!</h2>
                <div className="success-message">
                    <p>Thank you for your order!</p>
                    <p>We've received your request and will begin creating your custom content right away.</p>
                </div>
                {requestDetails && (
                    <div className="order-details">
                        <h3>Your Order Details:</h3>
                        <div className="detail-item">
                            <span className="detail-label">Request #:</span>
                            <span className="detail-value">{requestDetails.requestNumber || 'N/A'}</span>
                        </div>
                        <div className="detail-item">
                            <span className="detail-label">Top:</span>
                            <span className="detail-value">{requestDetails.topOutfitLabel || 'Not specified'}</span>
                        </div>
                        <div className="detail-item">
                            <span className="detail-label">Bottom:</span>
                            <span className="detail-value">{requestDetails.outfitLabel || 'Not specified'}</span>
                        </div>
                        <div className="detail-item">
                            <span className="detail-label">Pose:</span>
                            <span className="detail-value">{requestDetails.poseLabel || 'Not specified'}</span>
                        </div>
                        <div className="detail-item">
                            <span className="detail-label">Status:</span>
                            <span className="detail-value status-paid">Paid</span>
                        </div>
                        <div className="detail-item">
                            <span className="detail-label">Order ID:</span>
                            <span className="detail-value">{sessionId?.substring(0, 10)}...</span>
                        </div>
                    </div>
                )}
                <div className="next-steps">
                    <h3>What happens next?</h3>
                    <ul>
                        <li>Your custom content will be created within 5-7 business days</li>
                        <li>Your content will be available in your "Purchased Content" folder in Custom Builds</li>
                    </ul>
                </div>
                <button onClick={handleViewPurchased} className="return-button">
                    Track My Purchased Content
                </button>
            </div>
        </div>
    );
};

export default CustomSuccessPage;
